import { useEffect, useState, useRef } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from './ui/button'

// Simple, accessible banner shown at top when a new version is available
export default function UpdateBanner() {
  const [show, setShow] = useState(false)
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    const onUpdate = (e: Event) => {
      // CustomEvent with detail.registration from main.jsx
      const ce = e as CustomEvent<{ registration: ServiceWorkerRegistration }>
      registrationRef.current = ce.detail.registration
      setShow(true)
    }
    window.addEventListener('sw-update-available', onUpdate as EventListener)
    return () => window.removeEventListener('sw-update-available', onUpdate as EventListener)
  }, [])

  if (!show) return null

  const acceptAndReload = () => {
    const reg = registrationRef.current
    // Notify main.jsx that user accepted
    window.dispatchEvent(new Event('sw-accept-update'))
    try {
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' })
        setShow(false)
        try {
          window.location.reload()
        } catch {}
        return
      }
      // Fallback: trigger update check and then reload
      void reg
        ?.update()
        .catch(() => {})
        .finally(() => {
          setShow(false)
          try {
            window.location.reload()
          } catch {}
        })
    } catch {
      setShow(false)
      try {
        window.location.reload()
      } catch {}
    }
  }

  const dismiss = () => setShow(false)

  return (
    <div role="status" aria-live="polite" className="fixed top-0 left-0 right-0 z-50">
      <div className="mx-auto max-w-screen-2xl px-3 py-2">
        <div className="rounded-md bg-blue-50 border border-blue-200 text-blue-900 px-3 py-2 shadow-sm flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm font-medium">Neue Version verfügbar – bitte neu laden.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={dismiss}>Später</Button>
            <Button size="sm" onClick={acceptAndReload}>Neu laden</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
